import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress';
import Icon from '@material-ui/core/Icon';
import useTheme from '@material-ui/core/styles/useTheme';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useAuth } from 'src/app/infra/auth';
import { useRoute } from 'src/app/infra/routes';
import { getPerguntas } from 'src/app/services/votacao';
import { Loading, Msgbox, Optional } from 'src/app/shared/utils';
import { Pergunta, VotacaoItem } from 'src/app/types/votacao';
import { useWrapper } from 'src/utils/axios';
import Question from '../components/question';
import Megaphone from '../svg/icon_megaphone.svg';
import Answer from './answer';

const View = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const Header = styled(Row)`
  justify-content: space-between;
  padding: 0.5rem 2rem;
  img {
    margin-right: 1rem;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: none;
    padding: 0.5rem;
  }
`;

const Title = styled(Typography)`
  font-weight: bold;
  text-align: center;
  white-space: pre;
`;

const Text = styled(Typography)`
  text-align: center;
`;

const Section = styled.div`
  width: 100%;
  overflow: auto;
  height: 74vh;

  @media screen and (max-width: 480px), screen and (max-height: 420px) {
    height: 70vh;
  }
`;

const IconButton = styled(Button)`
  min-height: 28px;
`;

const Loader = styled(CircularProgress)`
  margin: 0 1rem;
  height: unset !important;
`;

const LinkAssembly = styled.a`
  padding: 4px;
  font-size: 14px;
  text-align: center;
  color: inherit;
`;

const TextAssembly = styled.span`
  color: currentColor;
  font-weight: bold;
`;

interface Props {
  data: VotacaoItem | undefined;
  onRefresh: (questions: Pergunta[]) => void;
}

export default ({ data, onRefresh }: Props) => {
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const { session } = useAuth();
  const {
    params: { condominio }
  } = useRoute();

  const response = useWrapper<Pergunta[]>();
  const [selected, setSelected] = useState<Pergunta>();

  const avaliarAssembleia =
    'https://form.typeform.com/to/Owe7y7bS#cond=' +
    condominio +
    '&unid=' +
    session?.user?.unidadeLogin?.unidade +
    '&email=' +
    session?.user?.email +
    '&data=' +
    data?.dataVotacao +
    '&orig=votacao';

  let pageInterval: any;
  let isPageActived = true;
  let statusKey = 'hidden';
  let eventKey = 'visibilitychange';

  const startInterval = () => {
    pageInterval = setInterval(
      () => isPageActived && !response.loading && refresh(),
      15000
    );
  };

  const stopInterval = () => {
    clearInterval(pageInterval);
  };

  const handleVisibility = () => {
    isPageActived = !document[statusKey];
  };

  const startWatcher = () => {
    if (document['webkitHidden']) {
      statusKey = 'webkitHidden';
      eventKey = 'webkitvisibilitychange';
    } else if (document['msHidden']) {
      statusKey = 'msHidden';
      eventKey = 'msvisibilitychange';
    }
    document.addEventListener(eventKey, handleVisibility, false);
  };

  const stopWatcher = () => {
    document.removeEventListener(eventKey, handleVisibility);
  };

  const refresh = () => {
    if (data) {
      response.exec(
        getPerguntas(data.codigoVotacao),
        (res) => onRefresh(res),
        undefined,
        {
          defaultErrorMsg:
            'Tivemos um problema ao carregar as perguntas, aguarde um momento!'
        }
      );
    }
  };

  const lengthNoVoted = () => {
    return response.payload?.filter((x) => !x.votoUsuario).length || 0;
  };

  const noHasAvaliarBtn = React.useMemo(
    () => ['7049', '7050', '7051'].includes(condominio),
    [condominio]
  );

  useEffect(() => {
    refresh();
    startInterval();
    startWatcher();
    return () => {
      stopInterval();
      stopWatcher();
      response.cancel();
    };
  }, []);

  return (
    <View>
      <Header>
        <Row>
          <img src={Megaphone} alt="icone pauta" />
          <Title
            color="primary"
            style={{
              color:
                (data?.identidadeVisual?.fontColor ?? '#000') < '#d3d3d3'
                  ? data?.identidadeVisual?.fontColor
                  : '#000'
            }}
          >
            Votação
          </Title>
        </Row>
        {response.loading && <Loader color="primary" />}
        <Column>
          <IconButton
            color="inherit"
            startIcon={
              <Icon
                style={{
                  color:
                    (data?.identidadeVisual?.fontColor ?? '#000') < '#d3d3d3'
                      ? data?.identidadeVisual?.fontColor
                      : '#000'
                }}
              >
                refresh
              </Icon>
            }
            onClick={() => refresh()}
          />
          <Title variant="caption">
            {lengthNoVoted() +
              ' / ' +
              (response.payload?.length || 0) +
              ' não votadas'}
          </Title>
        </Column>
      </Header>
      <Section>
        {response.payload &&
          response.payload.map((question, index) => (
            <Question
              key={index}
              item={question}
              selected={question.codigoPergunta === selected?.codigoPergunta}
              onSelect={(item) => setSelected(item)}
            >
              <Answer
                item={question}
                selected={question.codigoPergunta === selected?.codigoPergunta}
                resultType={data?.formaApresentacaoResultado}
                identidadeVisual={data?.identidadeVisual}
                onSuccess={() => refresh()}
              />
            </Question>
          ))}
        {!response.payload?.length && (
          <Text>
            Nenhuma pergunta disponível. Aguarde, em breve aparecerão aqui!
          </Text>
        )}
      </Section>
      {condominio === '2674' ? (
        <Button
          variant="contained"
          size="small"
          href="http://online.anyflip.com/tihx/pkqk/mobile/index.html"
          target="_blank"
          style={{
            backgroundColor: '#3cb5a9',
            color: '#fff',
            margin: '6px 0px',
            minHeight: '36px',
            textAlign: 'center'
          }}
        >
          Clique aqui para visualizar as previsões orçamentárias
        </Button>
      ) : null}
      {condominio === '2718' || condominio === '2719' ? (
        <Button
          variant="contained"
          size="small"
          href="http://online.anyflip.com/tihx/dmqe/mobile/"
          target="_blank"
          style={{
            backgroundColor: '#3cb5a9',
            color: '#fff',
            margin: '6px 0px',
            minHeight: '36px',
            textAlign: 'center'
          }}
        >
          Clique aqui para visualizar as previsões orçamentárias
        </Button>
      ) : null}
      {!noHasAvaliarBtn && (
        <LinkAssembly href={avaliarAssembleia} target="_blank">
          Antes de sair, <TextAssembly>clique aqui</TextAssembly> para avaliar a
          Assembleia Virtual
        </LinkAssembly>
      )}
      <Optional valid={isMobile}>
        <Loading open={response.loading} color="primary" />
      </Optional>
      <Msgbox error={response.error} onClose={() => response.reset()} />
    </View>
  );
};
